<script setup lang="ts">
import {
  $isCodeNode,
  CodeNode,
  getCodeLanguages,
  getDefaultCodeLanguage,
  getLanguageFriendlyName,
} from "@lexical/code";
import { mergeRegister } from "@lexical/utils";
import { useEventListener, useResizeObserver } from "@vueuse/core";
import { $createParagraphNode, $getNearestNodeFromDOMNode, $getNodeByKey, $getRoot } from "lexical";
import { useLexicalComposer } from "lexical-vue";
import { nextTick, onMounted, onUnmounted, ref, shallowRef } from "vue";

import Button from "~/components/dumb/Button.vue";
import EntityDropdownItem from "~/components/dumb/EntityDropdownItem.vue";
import MultiselectDropdownMenu from "~/components/dumb/MultiselectDropdownMenu.vue";
import Tooltip from "~/components/dumb/Tooltip.vue";
import { copyAndNotify } from "~/components/notifications";
import { ChevronDownIcon, CopyIcon } from "~/icons";
import { isDefined } from "~/shared/common";
import { ButtonStyle, IconSize } from "~/shared/enums";
import { usePageStore, useUserStore } from "~/stores";

const CODE_HEADER_HEIGHT = 32;
const SCROLLABLE_OVERFLOW = new Set(["auto", "overlay", "scroll"]);
const LANGUAGES = getCodeLanguages();
const FRIENDLY_LANGUAGES = new Set<string>();
const UNIQUE_LANGUAGES: string[] = [];
LANGUAGES.forEach((e) => {
  const friendly = getLanguageFriendlyName(e);
  if (FRIENDLY_LANGUAGES.has(friendly)) {
    return;
  }
  FRIENDLY_LANGUAGES.add(friendly);
  UNIQUE_LANGUAGES.push(e);
});

type CodeBlockControl = {
  clipPath?: string;
  height: string;
  key: string;
  language: string;
  left: string;
  top: string;
  width: string;
};

defineProps<{
  editable: boolean;
}>();

const pageStore = usePageStore();
const userStore = useUserStore();

const editor = useLexicalComposer();

const rootElement = ref<HTMLElement | null>(null);
const scrollElement = ref<HTMLElement | null>(null);
const controls = shallowRef<CodeBlockControl[]>([]);
const isNativeSticky = ref(true);

const copyCode = (key: string) => {
  let code = "";
  editor.read(() => {
    const node = $getNodeByKey(key);
    if ($isCodeNode(node)) {
      code = node.getTextContent();
    }
  });

  if (!code) {
    return;
  }

  copyAndNotify("Code", code);
};

const setSelectedLang = (key: string, lang: string) => {
  userStore.updateRecents({ lastCodingLanguage: lang });
  editor.update(() => {
    const node = $getNodeByKey(key);
    if ($isCodeNode(node)) {
      node.setLanguage(lang);
    }
  });
};

const createLanguageSections = (selectedLanguage: string) => [
  {
    title: "Languages",
    items: UNIQUE_LANGUAGES.map((lang) => ({
      value: lang,
      label: getLanguageFriendlyName(lang),
      selected: lang === selectedLanguage,
      disabled: false,
      component: EntityDropdownItem,
      componentArgs: { hideClose: true },
    })),
  },
];

const LANGUAGE_SECTIONS = new Map<string, ReturnType<typeof createLanguageSections>>();
const getLanguageSections = (selectedLanguage: string) => {
  const cachedSections = LANGUAGE_SECTIONS.get(selectedLanguage);
  if (cachedSections) {
    return cachedSections;
  }
  const sections = createLanguageSections(selectedLanguage);
  LANGUAGE_SECTIONS.set(selectedLanguage, sections);
  return sections;
};

const getScrollElement = (element: HTMLElement) => {
  for (let current: HTMLElement | null = element; current; current = current.parentElement) {
    if (
      SCROLLABLE_OVERFLOW.has(window.getComputedStyle(current).overflowY) &&
      current.scrollHeight > current.clientHeight
    ) {
      return current;
    }
  }
  return null;
};

let cachedScrollElement: HTMLElement | null | undefined;

const updateControls = () => {
  const root = rootElement.value;
  if (!root) {
    controls.value = [];
    return;
  }

  const offsetParent = root.closest<HTMLElement>(".dart-editor") ?? root;
  const rootRect = offsetParent.getBoundingClientRect();
  const currentScrollElement =
    cachedScrollElement === undefined ? (cachedScrollElement = getScrollElement(root)) : cachedScrollElement;
  const nativeSticky = currentScrollElement === null || currentScrollElement.contains(offsetParent);
  isNativeSticky.value = nativeSticky;
  scrollElement.value = nativeSticky ? null : currentScrollElement;
  const scrollRect = currentScrollElement?.getBoundingClientRect();
  const stickyTop = (scrollRect?.top ?? 0) - rootRect.top;
  const stickyBottom = (scrollRect?.bottom ?? 0) - rootRect.top;

  const nextControls: CodeBlockControl[] = [];
  editor.read(() => {
    root.querySelectorAll<HTMLElement>("code").forEach((element) => {
      const node = $getNearestNodeFromDOMNode(element);
      if (!$isCodeNode(node)) {
        return;
      }

      const rect = element.getBoundingClientRect();
      if (rect.width === 0 || rect.height === 0) {
        return;
      }
      if (!nativeSticky && scrollRect && (rect.bottom <= scrollRect.top || rect.top >= scrollRect.bottom)) {
        return;
      }

      const naturalTop = rect.top - rootRect.top;
      const codeBottom = naturalTop + rect.height;
      const top = nativeSticky
        ? naturalTop
        : Math.min(Math.max(naturalTop, stickyTop), codeBottom - CODE_HEADER_HEIGHT);
      const clipTop = nativeSticky ? 0 : Math.max(stickyTop - top, 0);
      const clipBottom = nativeSticky ? 0 : Math.max(top + CODE_HEADER_HEIGHT - stickyBottom, 0);
      const clipPath = clipTop || clipBottom ? `inset(${clipTop}px 0 ${clipBottom}px 0)` : undefined;

      nextControls.push({
        clipPath,
        height: `${nativeSticky ? rect.height : CODE_HEADER_HEIGHT}px`,
        key: node.getKey(),
        language: node.getLanguage() ?? getDefaultCodeLanguage(),
        left: `${rect.left - rootRect.left}px`,
        top: `${top}px`,
        width: `${rect.width}px`,
      });
    });
  });
  controls.value = nextControls;
};

let updateFrame: number | null = null;
const scheduleUpdateControls = () => {
  if (updateFrame !== null) {
    return;
  }
  updateFrame = window.requestAnimationFrame(() => {
    updateFrame = null;
    updateControls();
  });
};

const handleCodeNodeMutations = (mutatedNodes: Map<string, string>) => {
  const createdKeys = [...mutatedNodes.entries()].filter(([, action]) => action === "created").map(([key]) => key);
  if (createdKeys.length === 0) {
    return;
  }
  editor.update(() => {
    createdKeys.forEach((key) => {
      const node = $getNodeByKey(key);
      if ($isCodeNode(node) && !isDefined(node.getLanguage())) {
        node.setLanguage(userStore.recents.lastCodingLanguage ?? getDefaultCodeLanguage());
      }
    });
  });
};

const updateRootElement = (newRoot: HTMLElement | null) => {
  rootElement.value = newRoot;
  cachedScrollElement = undefined;
  scrollElement.value = null;

  if (!newRoot) {
    controls.value = [];
    return;
  }

  scheduleUpdateControls();
};

const handleResize = () => {
  cachedScrollElement = undefined;
  scheduleUpdateControls();
};

let unregisterListeners: () => void;

const onClick = (event: MouseEvent) => {
  if (event.target !== rootElement.value) {
    return;
  }
  editor.update(() => {
    const lastChild = $getRoot().getLastChild();
    if (!$isCodeNode(lastChild)) {
      return;
    }
    const paragraph = $createParagraphNode();
    lastChild.insertAfter(paragraph);
    paragraph.selectStart();
  });
};

const onAfterClose = async (key: string) => {
  editor.update(() => {
    const node = $getNodeByKey(key);
    if ($isCodeNode(node)) {
      node.selectEnd();
    }
  });
  await nextTick();
  rootElement.value?.focus();
};

useEventListener(rootElement, "click", onClick);
useEventListener(scrollElement, "scroll", scheduleUpdateControls);
useEventListener(window, "resize", handleResize);
useResizeObserver(rootElement, handleResize);

onMounted(() => {
  unregisterListeners = mergeRegister(
    editor.registerRootListener(updateRootElement),
    editor.registerMutationListener(CodeNode, handleCodeNodeMutations),
    editor.registerUpdateListener(scheduleUpdateControls)
  );
});

onUnmounted(() => {
  if (updateFrame !== null) {
    window.cancelAnimationFrame(updateFrame);
  }
  unregisterListeners?.();
});
</script>

<template>
  <div class="dart-code-selector contents print:hidden">
    <div
      v-for="control in controls"
      :key="control.key"
      :style="{
        top: control.top,
        left: control.left,
        width: control.width,
        height: control.height,
        clipPath: control.clipPath,
      }"
      class="pointer-events-none absolute">
      <div
        class="dart-code-selector-header z-10 flex h-8 items-center justify-between rounded-t border-b border-md px-1 text-md"
        :class="isNativeSticky ? 'sticky top-0' : 'relative'"
        @mousedown.prevent>
        <MultiselectDropdownMenu
          v-if="editable && !pageStore.isPublicView"
          :sections="getLanguageSections(control.language)"
          header-title="Set language"
          :close-on-select="!pageStore.isMobile"
          :on-after-close="() => onAfterClose(control.key)"
          @add="(lang) => setSelectedLang(control.key, lang)">
          <Button
            :text="getLanguageFriendlyName(control.language)"
            :btn-style="ButtonStyle.CHIP"
            class="!pointer-events-auto !h-6 !py-0 !pr-1 !pl-1.5 text-lt !focus-ring-std"
            text-style="text-xs"
            :icon-after="ChevronDownIcon"
            :icon-size="IconSize.XS"
            borderless />
        </MultiselectDropdownMenu>
        <span
          v-else
          class="flex h-6 items-center truncate border border-transparent pr-1 pl-1.5 text-xs text-lt select-none">
          {{ getLanguageFriendlyName(control.language) }}
        </span>

        <Tooltip text="Copy code">
          <Button
            :btn-style="ButtonStyle.CHIP"
            borderless
            :icon="CopyIcon"
            :icon-size="IconSize.XS"
            a11y-label="Copy"
            class="!pointer-events-auto !p-1 text-lt !focus-ring-std hover:!bg-md/80"
            @click="copyCode(control.key)" />
        </Tooltip>
      </div>
    </div>
  </div>
</template>

<style>
@reference "#frontend.css";
.dart-code-selector-header {
  @apply bg-[color-mix(in_oklab,var(--color-app-light-opposite)_5%,var(--color-app-light-std)_95%)] dark:bg-[color-mix(in_oklab,var(--color-app-dark-opposite)_5%,var(--color-app-dark-std)_95%)];
}

.dart-comment-editor .dart-code-selector-header,
.dart-comment-editor .dart-editor:has(> .dart-code-selector) .dart-code-block {
  @apply bg-[color-mix(in_oklab,var(--color-app-light-opposite)_15%,var(--color-app-light-std)_85%)] dark:bg-[color-mix(in_oklab,var(--color-app-dark-opposite)_15%,var(--color-app-dark-std)_85%)];
}

.dart-editor:has(> .dart-code-selector) .dart-code-block {
  @apply pt-10 pb-2 print:py-2;
}
</style>
